import { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Pencil, Play, Trash2 } from "lucide-react";
import { adminGet, adminSend, AdminApiError } from "../api.ts";
import {
  AdminForm,
  AdminFormActions,
  AdminFormModal,
  AdminFormSection,
} from "../components/AdminForm.tsx";
import { AdminTable } from "../components/AdminTable.tsx";
import { TablePagination } from "../components/TablePagination.tsx";
import { useClientPagination } from "../hooks/useClientPagination.ts";

interface PaymentInstanceSummary {
  readonly id: string;
  readonly provider: string;
  readonly instanceId: string;
  readonly baseUrl: string;
  readonly siteCode: string;
  readonly siteName: string | null;
  readonly currencyCode: string | null;
  readonly apiKey: string;
  readonly enabled: boolean;
  readonly createdAt: number;
  readonly updatedAt: number;
}

const listInstances = (): Promise<ReadonlyArray<PaymentInstanceSummary>> =>
  adminGet<ReadonlyArray<PaymentInstanceSummary>>("/payment/instances");

interface OnboardInput {
  readonly instanceId: string;
  readonly baseUrl: string;
  readonly siteCode: string;
  readonly siteName?: string;
  readonly currencyCode?: string;
  readonly apiKey: string;
  readonly secretKey: string;
}

const createInstance = (input: OnboardInput) =>
  adminSend<PaymentInstanceSummary>("POST", "/payment/instances", input);

const setEnabled = (id: string, enabled: boolean) =>
  adminSend<void>("PATCH", `/payment/instances/${id}`, { enabled });

const deleteInstance = (id: string) => adminSend<void>("DELETE", `/payment/instances/${id}`);

export const PaymentInstancesPage = () => {
  const qc = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [enabledFilter, setEnabledFilter] = useState<"all" | "on" | "off">("all");
  const [search, setSearch] = useState("");
  const { data: instances = [], isLoading } = useQuery({
    queryKey: ["payment-instances"],
    queryFn: listInstances,
    refetchInterval: 30_000,
  });
  const keyword = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    return instances.filter((inst) => {
      if (enabledFilter === "on" && !inst.enabled) return false;
      if (enabledFilter === "off" && inst.enabled) return false;
      if (keyword === "") return true;
      return (
        inst.instanceId.toLowerCase().includes(keyword) ||
        inst.siteCode.toLowerCase().includes(keyword) ||
        inst.baseUrl.toLowerCase().includes(keyword) ||
        (inst.siteName ?? "").toLowerCase().includes(keyword)
      );
    });
  }, [instances, enabledFilter, keyword]);
  const resetKey = `${enabledFilter}|${keyword}`;
  const { pageItems, page, setPage, pageSize, total, totalPages } = useClientPagination(
    filtered,
    resetKey,
  );

  const enableMut = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) => setEnabled(v.id, v.enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payment-instances"] });
      notifications.show({ message: "已更新", color: "green" });
    },
    onError: (e: AdminApiError) => notifications.show({ message: e.message, color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteInstance(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payment-instances"] });
      notifications.show({ message: "已删除", color: "green" });
    },
    onError: (e: AdminApiError) => notifications.show({ message: e.message, color: "red" }),
  });

  // 编辑实例：后端 PATCH 仅支持改 baseUrl（凭据变更请用相同 instanceId 重新创建）。
  const [editInst, setEditInst] = useState<PaymentInstanceSummary | null>(null);
  const [editBaseUrl, setEditBaseUrl] = useState("");
  const editMut = useMutation({
    mutationFn: (v: { id: string; baseUrl: string }) =>
      adminSend<void>("PATCH", `/payment/instances/${v.id}`, { baseUrl: v.baseUrl }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payment-instances"] });
      notifications.show({ message: "已更新 baseUrl", color: "green" });
      setEditInst(null);
    },
    onError: (e: AdminApiError) => notifications.show({ message: e.message, color: "red" }),
  });

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>支付实例</Title>
        <Button onClick={() => setWizardOpen(true)}>新增实例</Button>
      </Group>

      <Card withBorder>
        {isLoading ? (
          <Text>加载中...</Text>
        ) : instances.length === 0 ? (
          <Text c="dimmed">暂无实例，点「新增实例」录入 API 凭据。</Text>
        ) : (
          <>
            <Group gap="sm" mb="sm" wrap="wrap">
              <Select
                size="xs"
                label="状态"
                data={[
                  { label: "全部", value: "all" },
                  { label: "启用", value: "on" },
                  { label: "禁用", value: "off" },
                ]}
                value={enabledFilter}
                onChange={(v) => setEnabledFilter((v ?? "all") as "all" | "on" | "off")}
                style={{ width: 120 }}
                clearable={false}
              />
              <TextInput
                size="xs"
                label="搜索"
                placeholder="instanceId / siteCode / baseUrl"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ minWidth: 260, flex: 1 }}
              />
            </Group>
            <AdminTable minWidth={800}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>instanceId</Table.Th>
                  <Table.Th>baseUrl</Table.Th>
                  <Table.Th>子站</Table.Th>
                  <Table.Th>状态</Table.Th>
                  <Table.Th>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageItems.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" size="sm" ta="center">
                        无匹配实例
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
                {pageItems.map((inst) => (
                  <Table.Tr key={inst.id}>
                    <Table.Td>{inst.instanceId}</Table.Td>
                    <Table.Td>
                      <Text size="xs">{inst.baseUrl}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">
                        {inst.siteCode} {inst.currencyCode ?? ""}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={inst.enabled ? "green" : "gray"}>
                        {inst.enabled ? "启用" : "禁用"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label={inst.enabled ? "禁用" : "启用"} withArrow>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() =>
                              enableMut.mutate({ id: inst.id, enabled: !inst.enabled })
                            }
                          >
                            {inst.enabled ? <Pause size={14} /> : <Play size={14} />}
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="编辑 baseUrl" withArrow>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => {
                              setEditInst(inst);
                              setEditBaseUrl(inst.baseUrl);
                            }}
                          >
                            <Pencil size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="删除" withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => {
                              modals.openConfirmModal({
                                title: "确认删除",
                                children: `确认删除 ${inst.instanceId}？有 bot 引用则会失败。`,
                                labels: { confirm: "删除", cancel: "取消" },
                                confirmProps: { color: "red" },
                                onConfirm: () => deleteMut.mutate(inst.id),
                              });
                            }}
                          >
                            <Trash2 size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </AdminTable>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <OnboardWizard opened={wizardOpen} onClose={() => setWizardOpen(false)} />

      <AdminFormModal
        opened={editInst !== null}
        onClose={() => setEditInst(null)}
        title={editInst !== null ? `编辑实例：${editInst.instanceId}` : ""}
        size="lg"
      >
        <AdminForm
          onSubmit={(e) => {
            e.preventDefault();
            if (editInst !== null) {
              editMut.mutate({ id: editInst.id, baseUrl: editBaseUrl.trim() });
            }
          }}
        >
          <TextInput
            label="baseUrl"
            description="WG 后台地址变更时修改。API Key / Secret 变更请用相同 instanceId 重新创建（upsert 覆盖）。"
            placeholder="https://....cg.ink"
            value={editBaseUrl}
            onChange={(e) => setEditBaseUrl(e.currentTarget.value)}
          />
          <AdminFormActions>
            <Button variant="default" type="button" onClick={() => setEditInst(null)}>
              取消
            </Button>
            <Button
              type="submit"
              loading={editMut.isPending}
              disabled={editBaseUrl.trim() === "" || !editBaseUrl.startsWith("http")}
            >
              保存
            </Button>
          </AdminFormActions>
        </AdminForm>
      </AdminFormModal>
    </Stack>
  );
};

interface OnboardWizardProps {
  readonly opened: boolean;
  readonly onClose: () => void;
}

const OnboardWizard = ({ opened, onClose }: OnboardWizardProps) => {
  const qc = useQueryClient();
  const [result, setResult] = useState<PaymentInstanceSummary | null>(null);
  const form = useForm<OnboardInput>({
    initialValues: {
      instanceId: "",
      baseUrl: "",
      siteCode: "",
      siteName: "",
      currencyCode: "",
      apiKey: "",
      secretKey: "",
    },
    validate: {
      instanceId: (v) => (v.trim().length === 0 ? "必填" : null),
      baseUrl: (v) => (v.startsWith("http") ? null : "必须以 http(s) 开头"),
      siteCode: (v) => (v.trim().length === 0 ? "必填" : null),
      apiKey: (v) => (v.trim().length === 0 ? "必填" : null),
      secretKey: (v) => (v.length === 0 ? "必填" : null),
    },
  });

  const submit = useMutation({
    mutationFn: (values: OnboardInput) => {
      const payload: OnboardInput = {
        instanceId: values.instanceId.trim(),
        baseUrl: values.baseUrl.trim(),
        siteCode: values.siteCode.trim(),
        apiKey: values.apiKey.trim(),
        secretKey: values.secretKey,
        ...(values.siteName && values.siteName.trim() !== ""
          ? { siteName: values.siteName.trim() }
          : {}),
        ...(values.currencyCode && values.currencyCode.trim() !== ""
          ? { currencyCode: values.currencyCode.trim() }
          : {}),
      };
      return createInstance(payload);
    },
    onSuccess: (data) => {
      setResult(data);
      void qc.invalidateQueries({ queryKey: ["payment-instances"] });
      notifications.show({ message: "实例创建成功", color: "green" });
    },
    onError: (e: AdminApiError) =>
      notifications.show({ message: `失败: ${e.message}`, color: "red" }),
  });

  const handleClose = () => {
    form.reset();
    setResult(null);
    onClose();
  };

  return (
    <AdminFormModal opened={opened} onClose={handleClose} title="新增支付实例" size="xl">
      {result === null ? (
        <AdminForm onSubmit={form.onSubmit((vals) => submit.mutate(vals))}>
          <AdminFormSection title="实例标识">
            <SimpleGrid cols={2}>
              <TextInput
                label="instanceId (业务命名)"
                placeholder="例: ok11-jiqi1"
                {...form.getInputProps("instanceId")}
                required
              />
              <TextInput
                label="baseUrl"
                placeholder="例: https://api.example.com"
                {...form.getInputProps("baseUrl")}
                required
              />
            </SimpleGrid>
          </AdminFormSection>
          <AdminFormSection title="子站配置">
            <SimpleGrid cols={2}>
              <TextInput
                label="siteCode"
                placeholder="例: ok11"
                {...form.getInputProps("siteCode")}
                required
              />
              <TextInput
                label="siteName (可选)"
                placeholder="例: OK11 India"
                {...form.getInputProps("siteName")}
              />
              <TextInput
                label="currencyCode (可选)"
                placeholder="例: INR"
                {...form.getInputProps("currencyCode")}
              />
            </SimpleGrid>
          </AdminFormSection>
          <AdminFormSection title="API 凭据">
            <SimpleGrid cols={2}>
              <TextInput
                label="apiKey"
                placeholder="WG 官方 OpenAPI apiKey"
                {...form.getInputProps("apiKey")}
                required
              />
              <PasswordInput
                label="secretKey"
                placeholder="WG 官方 OpenAPI secretKey（不回显）"
                description="凭据变更请用相同 instanceId 重新创建（upsert 覆盖）。"
                {...form.getInputProps("secretKey")}
                required
              />
            </SimpleGrid>
          </AdminFormSection>
          <AdminFormActions>
            <Button variant="default" type="button" onClick={handleClose}>
              取消
            </Button>
            <Button type="submit" loading={submit.isPending}>
              创建实例
            </Button>
          </AdminFormActions>
        </AdminForm>
      ) : (
        <Stack gap="md">
          <Text fw={500}>实例已创建</Text>
          <Text size="sm">instanceId: {result.instanceId}</Text>
          <AdminTable minWidth={360}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>siteCode</Table.Th>
                <Table.Th>siteName</Table.Th>
                <Table.Th>currency</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>{result.siteCode}</Table.Td>
                <Table.Td>{result.siteName ?? ""}</Table.Td>
                <Table.Td>{result.currencyCode ?? ""}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </AdminTable>
          <AdminFormActions>
            <Button onClick={handleClose}>完成</Button>
          </AdminFormActions>
        </Stack>
      )}
    </AdminFormModal>
  );
};
